import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";

import {
  fetchUserById,
  updateUser,
  resetStatus,
} from "../../store/slices/userSlice";

import { fetchCountries } from "../../store/slices/locationSlice";

import StatusPopup from "../../components/popups/StatusPopup";

import EditSuccessPopup from "../../components/popups/EditSuccessPopup";

import EditPresnolDetail from "../../components/form/EditPresnolDetail";

import EditPayTaxes from "../../components/form/EditPayTaxes";

import EditAction from "../../components/form/EditAction";

const EditClient = () => {
  const { id } = useParams();

  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const location = useLocation();

  const EDIT_KEY = `editClient_${id}`;

  // USER DATA
  const selectedUser = useAppSelector(
    (s) => s.user.entityByKey[EDIT_KEY] || null,
  );

  const isInitialLoading = useAppSelector(
    (s) => s.user.entityLoadingByKey[EDIT_KEY] || false,
  );

  // UPDATE STATUS
  const {
    isSuccess,
    errorMessage,
    loading: isUpdating,
  } = useAppSelector((s) => s.user);

  // LOCATION
  const {
    countries = [],
    states = [],
    cities = [],
  } = useAppSelector((state) => state.location || {});

  const [clientFormData, setClientFormData] = useState({});

  const fromPath = location.state?.from || "/dashboard";

  // FETCH USER
  useEffect(() => {
    if (id) {
      dispatch(
        fetchUserById({
          id,
          key: EDIT_KEY,
        }),
      );
    }

    dispatch(resetStatus());

    return () => dispatch(resetStatus());
  }, [id, dispatch, EDIT_KEY]);

  // SET FORM DATA
  useEffect(() => {
    if (selectedUser) {
      setClientFormData(selectedUser);
    }
  }, [selectedUser]);

  // FETCH COUNTRIES
  useEffect(() => {
    dispatch(fetchCountries());
  }, [dispatch]);

  // POPUP CLOSE
  const handlePopupClose = () => {
    if (isSuccess) {
      navigate(fromPath, {
        replace: true,
      });
    }

    dispatch(resetStatus());
  };

  // FORM DATA CHANGE
  const handleDataChange = (updatedData) => {
    setClientFormData((prev) => ({
      ...prev,
      ...updatedData,
    }));
  };

  // UPDATE
  const handleUpdate = () => {
    if (!id || !clientFormData) return;

    const formData = new FormData();

    formData.append("name", clientFormData.name || "");

    formData.append("email", clientFormData.email || "");

    formData.append("phone", clientFormData.phone || "");

    formData.append("addressLine1", clientFormData.addressLine1 || "");

    formData.append(
      "cityId",
      clientFormData.cityId || clientFormData.CityId || "",
    );

    formData.append("postalCode", clientFormData.postalCode || "");

    formData.append("payrollCycle", clientFormData.payrollCycle || "");

    // TAX IDS
    const finalTaxIds =
      clientFormData.taxIds ||
      (Array.isArray(clientFormData.formulas)
        ? clientFormData.formulas.map((f) => (typeof f === "object" ? f.id : f))
        : []);

    if (finalTaxIds.length) {
      finalTaxIds.forEach((taxId) => {
        formData.append("taxIds", taxId);
      });
    }

    // IMAGE
    if (clientFormData.file) {
      formData.append("file", clientFormData.file);
    }

    dispatch(
      updateUser({
        id: Number(id),
        data: formData,
      }),
    );
  };

  // LOADER
  if (isInitialLoading && !selectedUser) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{
          height: "80vh",
        }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading</span>
        </div>

        <h5 className="ms-3 mb-0">Fetching Client Data</h5>
      </div>
    );
  }

  return (
    <>
      <div className={isUpdating ? "opacity-50 pointer-events-none" : ""}>
        <EditAction
          clientData={clientFormData}
          onUpdate={handleUpdate}
          isLoading={isUpdating}
        />

        <div>
          {/* PERSONAL DETAIL */}
          <EditPresnolDetail
            clientData={clientFormData}
            onDataChange={handleDataChange}
            // LOCATION
            countries={countries}
            states={states}
            cities={cities}
          />

          {/* TAX
          <EditPayTaxes
            clientData={
              clientFormData
            }
            onDataChange={
              handleDataChange
            }
            showTaxReminder={
              false
            }
          /> */}
        </div>
      </div>

      {/* SUCCESS */}
      <EditSuccessPopup
        show={isSuccess === true}
        onHide={handlePopupClose}
        entityType="client"
        operationType="update"
        title="Success"
      />

      {/* ERROR */}
      <StatusPopup
        show={!!errorMessage}
        type="error"
        title="Error"
        message={
          Array.isArray(errorMessage) ? errorMessage.join(", ") : errorMessage
        }
        handleClose={handlePopupClose}
      />
    </>
  );
};

export default EditClient;
